import { useDispatch, useSelector } from "react-redux";

import { RootState } from "../../store/store";
import { selectClusterList } from "../../store/slices/clusterListSlice";
import {
  addToCart,
  decreaseQuantity,
  increaseQuantity,
  removeFromCart,
} from "../../store/slices/cartSlice";
import {
  addToComplexOrderCart,
  decreaseComplexOrderCartQuantity,
  increaseComplexOrderCartQuantity,
  removeFromComplexOrderCart,
} from "../../store/slices/complexOrderCartSlice";
import { SaleItemInterface, KitItem } from "../types/interfaces";
import { useDisplayPrice } from "./useDisplayPrice";

export const useCartActions = (item: SaleItemInterface | null | undefined) => {
  const dispatch = useDispatch();
  const clusterList = useSelector(selectClusterList);

  const { items: cartItems } = useSelector((state: RootState) => state.cart);
  const { items: complexItems } = useSelector(
    (state: RootState) => state.complexOrderCart,
  );

  const getPrice = useDisplayPrice();

  const clusterId = item?.clusterId ? Number(item.clusterId) : 0;
  const cluster = clusterList.find((c) => c.id === clusterId);

  const isComplexCategory = item?.category?.isComplex ?? false;

  const hasRegularSales = cluster?.regularSales === true;
  const hasComplexSales = cluster?.complexSales === true;

  const cartId = item ? `${clusterId}_${item.id}` : "";

  const cartItem = cartItems.find((i) => i.id === cartId);
  const complexItem = complexItems.find(
    (i) =>
      item && i.itemId === item.id && i.clusterId === clusterId && !i.parentId,
  );

  const quantity = cartItem?.quantity || 0;

  const price = getPrice(item?.prices);
  const hasPrice = Boolean(price) && price > 0;
  const hasChildren = (item?.items?.length ?? 0) > 0;

  const isComplexWithoutPrice = isComplexCategory && !hasPrice && hasChildren;

  const showCartButton = isComplexCategory
    ? hasRegularSales && isComplexWithoutPrice
    : hasRegularSales && hasPrice;

  const showComplexButton = hasComplexSales;

  console.log("CART DEBUG:", {
    clusterId,
    clusterName: cluster?.name,
    clusterFound: !!cluster,
    regularSales: cluster?.regularSales,
    complexSales: cluster?.complexSales,
    hasRegularSales,
    hasComplexSales,
    isComplexCategory,
    hasPrice,
    hasChildren,
    isComplexWithoutPrice,
    showCartButton,
    showComplexButton,
  });

  const finalQuantity = showComplexButton && showCartButton ? 0 : quantity;

  const getFirstImage = (files: string[] | undefined) => {
    if (!files || !Array.isArray(files)) return "";
    const image = files.find(
      (path) => !path.includes("video_") && !path.includes("poster_"),
    );
    if (image) return image;
    const poster = files.find((path) => path.includes("poster_"));
    if (poster) return poster;
    return files[0] || "";
  };

  const image = getFirstImage(item?.files);

  const handleAddToCart = () => {
    if (!item) return;

    if (!hasPrice && hasChildren) {
      item.items!.forEach((kitItem: any) => {
        const child = kitItem.childItem || kitItem.item;
        if (!child) return;

        const childPrice = getPrice(child.prices);
        if (childPrice <= 0) return;

        const childImage = getFirstImage(child.files);
        const childCartId = `${child.clusterId}_${child.id}`;
        const qty = kitItem.quantity || 1;

        for (let i = 0; i < qty; i++) {
          dispatch(
            addToCart({
              id: childCartId,
              saleItemId: child.id,
              clusterId: child.clusterId,
              name: child.name || "",
              price: childPrice,
              image: childImage,
              minQuantity: child.minQuantity || 1,
              quantityStep: child.quantityStep || 1,
              unit: child.unit,
              attributes: child.attributes || [],
            }),
          );
        }
      });
      return;
    }

    dispatch(
      addToCart({
        id: cartId,
        saleItemId: item.id,
        clusterId,
        name: item.name || "",
        price,
        image,
        minQuantity: item.minQuantity || 1,
        quantityStep: item.quantityStep || 1,
        unit: item.unit,
        attributes: item.attributes || [],
      }),
    );
  };

  const handleAddToComplex = () => {
    if (!item) return;

    if (complexItem) {
      dispatch(removeFromComplexOrderCart(complexItem.id));
      return;
    }

    dispatch(
      addToComplexOrderCart({
        itemId: item.id,
        clusterId,
        name: item.name || "",
        price,
        image,
        categoryId: Number(item.categoryId) || undefined,
        category: item.category,
        minQuantity: item.minQuantity || 1,
        quantityStep: item.quantityStep || 1,
        unit: item.unit,
        attributes: item.attributes || [],
      }),
    );

    const kits: KitItem[] = item.items || [];
    if (kits.length > 0) {
      kits.forEach((kitItem) => {
        const child = (kitItem as any).childItem || kitItem.item;
        if (!child) return;

        const childPrice = getPrice(child.prices);
        if (childPrice <= 0) return;

        const childImage = getFirstImage(child.files);

        dispatch(
          addToComplexOrderCart({
            itemId: child.id,
            clusterId,
            name: child.name || "",
            price: childPrice,
            image: childImage,
            categoryId: Number(child.categoryId) || undefined,
            category: child.category,
            minQuantity: child.minQuantity || 1,
            quantityStep: child.quantityStep || 1,
            parentId: cartId,
            unit: child.unit,
            attributes: child.attributes || [],
          }),
        );
      });
    }
  };

  const handleIncrease = () => {
    if (isComplexCategory && complexItem) {
      dispatch(increaseComplexOrderCartQuantity(complexItem.id));
    } else if (cartItem) {
      dispatch(increaseQuantity(cartItem.id));
    }
  };

  const handleDecrease = () => {
    if (isComplexCategory && complexItem) {
      dispatch(decreaseComplexOrderCartQuantity(complexItem.id));
    } else if (cartItem) {
      dispatch(decreaseQuantity(cartItem.id));
    }
  };

  const handleRemove = () => {
    if (isComplexCategory && complexItem) {
      dispatch(removeFromComplexOrderCart(complexItem.id));
    } else if (cartItem) {
      dispatch(removeFromCart(cartItem.id));
    }
  };

  return {
    quantity: finalQuantity,
    isComplexCategory,
    showComplexButton,
    showCartButton,
    isInComplexCart: !!complexItem,
    handleAddToCart,
    handleAddToComplex,
    handleIncrease,
    handleDecrease,
    handleRemove,
  };
};